import { getStateStore } from '../../stores';
import { invitationStateV2, notificationInAppState, playjectorHostModeState, adModeState } from '../../stores/shared';
import { invalidateInvitation } from '../managers/invitation';
import { playjectorStartPavilion, sendMatchmakerHostConfig, sendHostReady } from '../managers/matchmaker';
import { notifyStreamStarting } from '../managers/notifications';
import { startStream } from '../peers/host';
import { sendPlayerJoinMacro } from '../managers/macroManager';
import { USE_PLAYJECTOR_COORDINATOR } from '../../../config/featureFlags';

// Debug flag to bypass Steam launch waiting
const DEBUG_SKIP_STEAM_WAIT = true;

const state = getStateStore('ipc');

const waitForGameToLaunch = (timeoutMs: number) => {
  return new Promise((resolve) => {
    // Check if ad mode is enabled
    const isAdMode = adModeState.currentState.enabled;

    if (!isAdMode) {
      // Original behavior: check Steam Big Picture
      if (DEBUG_SKIP_STEAM_WAIT) {
        console.warn('[DEBUG] Skipping Steam launch wait');
        resolve(true);
        return;
      }

      const steamLaunched = playjectorHostModeState.currentState.steamLaunched;
      const steamWindow = playjectorHostModeState.currentState.steamWindow;
      const userLoggedIn = playjectorHostModeState.currentState.userLoggedIn;

      // If a user is logged in, we know we are in big picture.
      if (steamLaunched && steamWindow && userLoggedIn) {
        resolve(true);
        return;
      }

      // Wait for the user to be ready.
      const steamListener = playjectorHostModeState.addListener((playjectorState) => {
        if (!playjectorState.steamLaunched || !playjectorState.steamWindow || !playjectorState.userLoggedIn) {
          return;
        }

        if (steamListener) playjectorHostModeState.removeListener(steamListener);
        resolve(true);
      });

      // A timeout for stuck connections or broken states. Todo: make this invalidate the lobby.
      setTimeout(() => {
        if (steamListener) playjectorHostModeState.removeListener(steamListener);
        resolve(true);
      }, timeoutMs);
    } else {
      // NEW: Ad mode behavior - check gameLaunched
      const gameLaunched = playjectorHostModeState.currentState.gameLaunched;

      if (gameLaunched) {
        console.log('[Ad Mode] Game already launched');
        resolve(true);
        return;
      }

      console.log('[Ad Mode] Waiting for game to launch...');
      const gameListener = playjectorHostModeState.addListener((playjectorState) => {
        if (playjectorState.gameLaunched) {
          console.log('[Ad Mode] Game launched!');
          if (gameListener) playjectorHostModeState.removeListener(gameListener);
          resolve(true);
        }
      });

      setTimeout(() => {
        console.warn('[Ad Mode] Game launch timeout');
        if (gameListener) playjectorHostModeState.removeListener(gameListener);
        resolve(false);
      }, timeoutMs);
    }
  });
}

export const initializeMatchmakerEvents = () => {
  let cachedMatchmakerState = state.matchmakerState.currentState.state;
  state.matchmakerState.addListener((matchmakerState) => {
    console.log('[MATCHMAKER-STATE] State:', matchmakerState);
    if (matchmakerState.state !== cachedMatchmakerState) {
      // In app notification
      if (matchmakerState.state === 'MATCHMAKING_DISABLED' && matchmakerState.stateDetails?.currentMatchInvitationId) {
        // Wait for invitation to be updated, with a 1 second timeout to just start anyway.
        new Promise((resolve) => {
          if (invitationStateV2.currentState.id === matchmakerState.stateDetails?.currentMatchInvitationId) {
            resolve(true);
            return;
          }
          const listener = invitationStateV2.addListener((invitationState) => {
            if (invitationState.id === matchmakerState.stateDetails?.currentMatchInvitationId) {
              if (listener) invitationStateV2.removeListener(listener);
              resolve(true);
            }
          });
          setTimeout(() => {
            if (listener) invitationStateV2.removeListener(listener);
            resolve(true);
          }, 1000);
        }).then(() => {
          setTimeout(() => {
            // Only launch pavilion if NOT in ad mode (game already launched)
            if (!adModeState.currentState.enabled) {
              playjectorStartPavilion();
            }

            waitForGameToLaunch(3 * 60 * 1000).then(async (success) => { // 3 minutes.
              // Check if ad mode - handle failures differently
              if (adModeState.currentState.enabled) {
                if (!success) {
                  invalidateInvitation('Game failed to launch');
                  return;
                }
              } else {
                // Original Steam check
                if (!DEBUG_SKIP_STEAM_WAIT && !playjectorHostModeState.currentState.userLoggedIn) {
                  invalidateInvitation('Playjector failed steam login');
                  return;
                }
              }

              // In ad mode: run the join macro (capped at 3s) then signal
              // HOST_READY. If the macro is short it completes normally;
              // if it exceeds the cap we proceed anyway (macro keeps
              // running in the background) so the client receives the
              // broadcastId well before the 15s timeout.
              if (adModeState.currentState.enabled) {
                // TODO-STRANGLER-FIG: Legacy macro call - disabled when coordinator handles macros
                if (!USE_PLAYJECTOR_COORDINATOR) {
                  await sendPlayerJoinMacro();
                }
                sendHostReady();
              }

              // Then start the stream
              startStream();
              notifyStreamStarting();
            });
          }, 50);
        });
      }
    }
    cachedMatchmakerState = matchmakerState.state;
  });
};
